import { Player, TeamInLeague, TacticalPosition, Tactics } from '../domain/entities';
import { calculateMatchPerformance, calculatePositionalPenalty } from '../utils/utilities';
import { FORMATIONS, POSITION_MAP } from '../domain/constants';

/**
 * Offline AI Service
 * Makes intelligent decisions without using external APIs
 * Designed to simulate realistic human gameplay for testing and demonstration
 */

interface OfflineAiActions {
    reasoning: string;
    lineupChanges: Array<{ playerInId: number; playerOutId: number; }>;
    promotions: Array<{ playerId: number; }>;
    playersToSell: Array<{ playerId: number; }>;
    playersToBuy: Array<{ playerId: number; }>;
    playerConversations: Array<{ playerId: number; choice: 'praise' | 'promise' | 'show_faith' | 'demand_improvement' | 'warn_dropped' | 'dismiss_concerns' | 'assert_authority'; reason: string; }>;
}

// Calculate enhanced performance including morale
const calculateEnhancedPerformance = (player: Player): number => {
    const basePerf = calculateMatchPerformance(player);
    const moraleModifiers: Record<string, number> = {
        'Ecstatic': 3,
        'Happy': 1.5,
        'Content': 0,
        'Unhappy': -2,
        'Furious': -4
    };
    const moraleBonus = moraleModifiers[player.morale] || 0;
    return basePerf + moraleBonus;
};

// Calculate final performance for a specific tactical position
const calculateFinalPerformance = (player: Player, position: TacticalPosition): number => {
    const enhancedPerf = calculateEnhancedPerformance(player);
    const penalty = calculatePositionalPenalty(player, position);
    return enhancedPerf * (1 - penalty);
};

// Find best lineup for current formation
const findBestLineup = (squad: Player[], formation: string): Tactics['lineup'] => {
    const lineup: Tactics['lineup'] = {};
    const formationPositions = FORMATIONS[formation];
    if (!formationPositions) return {};

    const availablePlayers = [...squad].filter(p => p.injury?.type !== 'Severe');

    // Assign each position to the best available player
    formationPositions.forEach(pos => {
        let bestPlayerIndex = -1;
        let bestScore = -Infinity;

        availablePlayers.forEach((player, index) => {
            const finalPerf = calculateFinalPerformance(player, pos);
            if (finalPerf > bestScore) {
                bestScore = finalPerf;
                bestPlayerIndex = index;
            }
        });

        if (bestPlayerIndex !== -1) {
            lineup[pos] = availablePlayers[bestPlayerIndex].id;
            availablePlayers.splice(bestPlayerIndex, 1);
        } else {
            lineup[pos] = null;
        }
    });

    return lineup;
};

// Find lineup improvements
const findLineupImprovements = (squad: Player[], currentLineup: Tactics['lineup'], formation: string): Array<{ playerInId: number; playerOutId: number; reason: string; }> => {
    const improvements: Array<{ playerInId: number; playerOutId: number; reason: string; }> = [];
    const formationPositions = FORMATIONS[formation];
    if (!formationPositions) return improvements;

    const starterIds = new Set(Object.values(currentLineup).filter(id => id !== null));
    const reserves = squad.filter(p => !starterIds.has(p.id) && p.injury?.type !== 'Severe');

    // Check each position for potential improvements
    for (const position of formationPositions) {
        const currentPlayerId = currentLineup[position];
        if (!currentPlayerId) continue;

        const currentPlayer = squad.find(p => p.id === currentPlayerId);
        if (!currentPlayer) continue;

        const currentPerf = calculateFinalPerformance(currentPlayer, position);

        // Look for better reserve players
        for (const reserve of reserves) {
            const reservePerf = calculateFinalPerformance(reserve, position);
            const perfDifference = reservePerf - currentPerf;

            // Consider swapping if performance improvement is significant (>2 points)
            if (perfDifference > 2) {
                let reason = '';

                // Determine reason for swap
                if (currentPlayer.injury?.type === 'Light') {
                    reason = `${currentPlayer.name} is carrying an injury`;
                } else if (currentPlayer.fitness < 60 && reserve.fitness > currentPlayer.fitness + 20) {
                    reason = `${currentPlayer.name} is tired (${currentPlayer.fitness}% fitness)`;
                } else if (currentPlayer.form < -2 && reserve.form > currentPlayer.form + 2) {
                    reason = `${reserve.name} is in better form (+${reserve.form} vs ${currentPlayer.form})`;
                } else if (currentPlayer.morale === 'Furious' || currentPlayer.morale === 'Unhappy') {
                    reason = `${currentPlayer.name} has poor morale (${currentPlayer.morale})`;
                } else {
                    reason = `${reserve.name} offers better overall performance`;
                }

                improvements.push({
                    playerInId: reserve.id,
                    playerOutId: currentPlayer.id,
                    reason
                });
                break; // Only suggest one improvement per position
            }
        }
    }

    return improvements;
};

// Find youth players worth promoting
const findPromotionCandidates = (youthSquad: Player[], seniorSquad: Player[], maxSquadSize: number): Array<{ playerId: number; reason: string; }> => {
    const promotions: Array<{ playerId: number; reason: string; }> = [];

    // Don't promote if squad is full
    if (seniorSquad.length >= maxSquadSize) return promotions;

    const slotsAvailable = maxSquadSize - seniorSquad.length;
    const avgSeniorSkill = seniorSquad.reduce((sum, p) => sum + p.skill, 0) / (seniorSquad.length || 1);

    // Sort youth players by skill + potential
    const candidates = [...youthSquad]
        .filter(p => p.age >= 16) // Only promote players 16+
        .sort((a, b) => (b.skill + b.potential / 2) - (a.skill + a.potential / 2));

    for (let i = 0; i < Math.min(slotsAvailable, candidates.length); i++) {
        const youth = candidates[i];

        // Promote if youth player is good enough
        if (youth.skill >= avgSeniorSkill - 5 || youth.potential > avgSeniorSkill + 10) {
            let reason = '';
            if (youth.potential > avgSeniorSkill + 15) {
                reason = `${youth.name} has exceptional potential (${youth.potential})`;
            } else if (youth.skill >= avgSeniorSkill) {
                reason = `${youth.name} is ready for senior football (skill: ${youth.skill})`;
            } else {
                reason = `${youth.name} shows promise and squad has space`;
            }

            promotions.push({ playerId: youth.id, reason });
        }
    }

    return promotions;
};

// Find players worth selling
const findSaleCandidates = (squad: Player[], avgSkill: number): Array<{ playerId: number; reason: string; }> => {
    const sales: Array<{ playerId: number; reason: string; }> = [];

    // Don't sell if squad is small
    if (squad.length < 18) return sales;

    // Sort by value - poorest performers first
    const candidates = [...squad]
        .filter(p => {
            // Don't sell key players or young talents
            if (p.age < 21 && p.potential > avgSkill + 10) return false;
            if (p.skill > avgSkill + 5) return false;
            return true;
        })
        .sort((a, b) => (a.skill + a.form) - (b.skill + b.form));

    // Consider selling 1-2 players if they're significantly below average
    for (let i = 0; i < Math.min(2, candidates.length); i++) {
        const player = candidates[i];

        if (player.skill < avgSkill - 8) {
            const reason = `${player.name} is well below squad average (skill: ${player.skill} vs ${Math.round(avgSkill)})`;
            sales.push({ playerId: player.id, reason });
        } else if (player.age > 32 && player.skill < avgSkill) {
            const reason = `${player.name} is aging (${player.age}) and below average`;
            sales.push({ playerId: player.id, reason });
        }
    }

    return sales;
};

// Find transfer targets
const findTransferTargets = (
    availablePlayers: Array<{ player: Player; askingPrice: number; }>,
    currentSquad: Player[],
    budget: number,
    maxSquadSize: number
): Array<{ playerId: number; reason: string; }> => {
    const purchases: Array<{ playerId: number; reason: string; }> = [];

    // Don't buy if squad is full or budget is low
    if (currentSquad.length >= maxSquadSize || budget < 50000) return purchases;

    const avgSkill = currentSquad.reduce((sum, p) => sum + p.skill, 0) / (currentSquad.length || 1);
    const slotsAvailable = maxSquadSize - currentSquad.length;

    // Find affordable upgrades
    const targets = availablePlayers
        .filter(({ player, askingPrice }) => {
            // Must be affordable (< 40% of budget)
            if (askingPrice > budget * 0.4) return false;
            // Must be an improvement
            if (player.skill <= avgSkill) return false;
            // Avoid old players
            if (player.age > 30) return false;
            return true;
        })
        .sort((a, b) => {
            // Prioritize best value (skill per £)
            const aValue = a.player.skill / (a.askingPrice || 1);
            const bValue = b.player.skill / (b.askingPrice || 1);
            return bValue - aValue;
        });

    // Buy 1-2 players max per window
    for (let i = 0; i < Math.min(slotsAvailable, 2, targets.length); i++) {
        const { player, askingPrice } = targets[i];
        const skillImprovement = player.skill - avgSkill;

        let reason = '';
        if (skillImprovement > 10) {
            reason = `${player.name} is a significant upgrade (skill: ${player.skill} vs avg: ${Math.round(avgSkill)})`;
        } else if (player.potential > avgSkill + 15) {
            reason = `${player.name} has high potential (${player.potential}) for the future`;
        } else {
            reason = `${player.name} offers good value at £${askingPrice.toLocaleString()}`;
        }

        purchases.push({ playerId: player.id, reason });

        // Update budget for next iteration
        budget -= askingPrice;
        if (budget < 50000) break;
    }

    return purchases;
};

// Find players who need morale management through conversation
const findPlayerConversations = (squad: Player[], currentLineup: Tactics['lineup'], now: { season: number; week: number }): Array<{ playerId: number; choice: 'praise' | 'promise' | 'show_faith' | 'demand_improvement' | 'warn_dropped' | 'dismiss_concerns' | 'assert_authority'; reason: string; }> => {
    const conversations: Array<{ playerId: number; choice: 'praise' | 'promise' | 'show_faith' | 'demand_improvement' | 'warn_dropped' | 'dismiss_concerns' | 'assert_authority'; reason: string; }> = [];

    const starterIds = new Set(Object.values(currentLineup).filter(id => id !== null));

    squad.forEach(player => {
        const isStarter = starterIds.has(player.id);

        // Check if player is on conversation cooldown (4-week per-player cooldown)
        const playerCooldown = player.nextConversationAvailable;
        const isOnCooldown = playerCooldown && (now.season < playerCooldown.season || (now.season === playerCooldown.season && now.week < playerCooldown.week));

        // Skip players on cooldown
        if (isOnCooldown) {
            return;
        }

        // Handle unhappy/furious players
        if (player.morale === 'Unhappy' || player.morale === 'Furious') {
            if (isStarter) {
                // Starting XI players who are unhappy - show faith or promise improvement
                if (player.form === 'Poor') {
                    conversations.push({
                        playerId: player.id,
                        choice: 'show_faith',
                        reason: `${player.name} is unhappy despite being in the starting XI. Showing faith to boost morale.`
                    });
                } else {
                    conversations.push({
                        playerId: player.id,
                        choice: 'praise',
                        reason: `${player.name} (${player.morale}) is a key player. Praising to improve morale.`
                    });
                }
            } else {
                // Reserve players who are unhappy - promise playing time
                conversations.push({
                    playerId: player.id,
                    choice: 'promise',
                    reason: `${player.name} is unhappy on the bench. Promising more playing time.`
                });
            }
        }

        // Praise players in excellent form
        if (player.form === 'Excellent' && player.morale === 'Content' && isStarter) {
            conversations.push({
                playerId: player.id,
                choice: 'praise',
                reason: `${player.name} is in excellent form. Praising to boost morale further.`
            });
        }

        // Demand improvement from underperforming starters
        if (player.form === 'Poor' && isStarter && (player.morale === 'Content' || player.morale === 'Happy')) {
            conversations.push({
                playerId: player.id,
                choice: 'demand_improvement',
                reason: `${player.name} is in poor form. Demanding improvement to motivate.`
            });
        }
    });

    // Limit to 2 conversations per week (same as human player) to keep it realistic
    return conversations.slice(0, 2);
};

export const OfflineAiService = {
    /**
     * Generate AI player actions without using external APIs
     * Makes intelligent decisions based on game state
     */
    generateAiPlayerActions: (
        team: TeamInLeague,
        youthSquad: Player[],
        transferMarket: Array<{ player: Player; askingPrice: number; }>,
        budget: number,
        maxSquadSize: number,
        isManagementWeek: boolean,
        now?: { season: number; week: number } // Current game time for cooldown checks
    ): OfflineAiActions => {
        console.log('🤖 [AI Player] Starting decision-making process...');
        console.log('🤖 [AI Player] Team:', team.name);
        console.log('🤖 [AI Player] Budget:', `£${budget.toLocaleString()}`);
        console.log('🤖 [AI Player] Squad size:', `${team.squad.length}/${maxSquadSize}`);
        console.log('🤖 [AI Player] Youth academy size:', youthSquad.length);
        console.log('🤖 [AI Player] Transfer market size:', transferMarket.length);
        console.log('🤖 [AI Player] Management week:', isManagementWeek);

        const avgSkill = team.squad.reduce((sum, p) => sum + p.skill, 0) / (team.squad.length || 1);
        console.log('🤖 [AI Player] Average squad skill:', avgSkill.toFixed(1));

        // ALWAYS use auto-fill best XI to get optimal lineup
        console.log('🤖 [AI Player] Calculating best possible lineup (Auto-Fill Best XI)...');
        const bestLineup = findBestLineup(team.squad, team.tactics.formation);

        // Compare with current lineup to generate changes
        const lineupChanges: Array<{ playerInId: number; playerOutId: number; reason: string; }> = [];
        const formationPositions = FORMATIONS[team.tactics.formation];
        if (formationPositions) {
            for (const position of formationPositions) {
                const currentPlayerId = team.tactics.lineup[position];
                const bestPlayerId = bestLineup[position];

                if (currentPlayerId !== bestPlayerId && bestPlayerId !== null) {
                    const currentPlayer = team.squad.find(p => p.id === currentPlayerId);
                    const bestPlayer = team.squad.find(p => p.id === bestPlayerId);

                    if (currentPlayer && bestPlayer) {
                        const currentPerf = calculateFinalPerformance(currentPlayer, position);
                        const bestPerf = calculateFinalPerformance(bestPlayer, position);

                        lineupChanges.push({
                            playerInId: bestPlayerId,
                            playerOutId: currentPlayerId,
                            reason: `Better performance at ${position}: ${bestPlayer.name} (${bestPerf.toFixed(1)}) > ${currentPlayer.name} (${currentPerf.toFixed(1)})`
                        });
                    }
                }
            }
        }

        console.log('🤖 [AI Player] Found', lineupChanges.length, 'lineup changes for optimal XI');
        if (lineupChanges.length > 0) {
            console.log('🤖 [AI Player] Recommended lineup changes:', lineupChanges.map(c => c.reason).join(', '));
        } else {
            console.log('🤖 [AI Player] Current lineup is already optimal!');
        }

        let reasoning = '';
        let promotions: Array<{ playerId: number; }> = [];
        let playersToSell: Array<{ playerId: number; }> = [];
        let playersToBuy: Array<{ playerId: number; }> = [];
        let playerConversations: Array<{ playerId: number; choice: 'praise' | 'promise' | 'show_faith' | 'demand_improvement' | 'warn_dropped' | 'dismiss_concerns' | 'assert_authority'; reason: string; }> = [];

        // ALWAYS check for players needing morale management
        console.log('🤖 [AI Player] 💬 Checking squad morale...');
        playerConversations = findPlayerConversations(team.squad, team.tactics.lineup, now || { season: 1, week: 1 });
        if (playerConversations.length > 0) {
            console.log('🤖 [AI Player] ✅ Found', playerConversations.length, 'player(s) needing conversation:');
            playerConversations.forEach(c => {
                const player = team.squad.find(p => p.id === c.playerId);
                if (player) {
                    console.log(`🤖 [AI Player]   - ${player.name} (Morale: ${player.morale}, Form: ${player.form})`);
                    console.log(`🤖 [AI Player]     Action: ${c.choice}`);
                    console.log(`🤖 [AI Player]     Reason: ${c.reason}`);
                }
            });
        } else {
            console.log('🤖 [AI Player] ✅ Squad morale is stable');
        }
        console.log('');

        if (isManagementWeek) {
            // Management week: Consider transfers and promotions
            console.log('🤖 [AI Player] === STRATEGIC REVIEW ===');
            console.log('🤖 [AI Player] Current squad status: Size:', team.squad.length, '/', maxSquadSize);
            console.log('🤖 [AI Player] Youth academy: Size:', youthSquad.length);
            console.log('🤖 [AI Player] Available transfer market:', transferMarket.length, 'players');
            console.log('');

            console.log('🤖 [AI Player] 📊 Evaluating promotion candidates...');
            const promotionCandidates = findPromotionCandidates(youthSquad, team.squad, maxSquadSize);
            if (promotionCandidates.length > 0) {
                console.log('🤖 [AI Player] ✅ Found', promotionCandidates.length, 'worthy promotion candidate(s):');
                promotionCandidates.forEach(c => {
                    const player = youthSquad.find(p => p.id === c.playerId);
                    if (player) {
                        console.log(`🤖 [AI Player]   - ${player.name} (Age: ${player.age}, Skill: ${player.skill}, Potential: ${player.potential})`);
                        console.log(`🤖 [AI Player]     Reason: ${c.reason}`);
                    }
                });
            } else {
                console.log('🤖 [AI Player] ❌ No youth players ready for promotion');
            }
            console.log('');

            console.log('🤖 [AI Player] 💰 Evaluating players to sell...');
            const saleCandidates = findSaleCandidates(team.squad, avgSkill);
            if (saleCandidates.length > 0) {
                console.log('🤖 [AI Player] ✅ Found', saleCandidates.length, 'player(s) to consider selling:');
                saleCandidates.forEach(c => {
                    const player = team.squad.find(p => p.id === c.playerId);
                    if (player) {
                        console.log(`🤖 [AI Player]   - ${player.name} (Age: ${player.age}, Skill: ${player.skill}, Form: ${player.form})`);
                        console.log(`🤖 [AI Player]     Reason: ${c.reason}`);
                    }
                });
            } else {
                console.log('🤖 [AI Player] ❌ No players worth selling at this time');
            }
            console.log('');

            console.log('🤖 [AI Player] 🛒 Searching transfer market...');
            const transferTargets = findTransferTargets(transferMarket, team.squad, budget, maxSquadSize);
            if (transferTargets.length > 0) {
                console.log('🤖 [AI Player] ✅ Found', transferTargets.length, 'transfer target(s):');
                transferTargets.forEach(t => {
                    const playerInfo = transferMarket.find(p => p.player.id === t.playerId);
                    if (playerInfo) {
                        console.log(`🤖 [AI Player]   - ${playerInfo.player.name} (Skill: ${playerInfo.player.skill}, Price: £${playerInfo.askingPrice.toLocaleString()})`);
                        console.log(`🤖 [AI Player]     Reason: ${t.reason}`);
                    }
                });
            } else {
                console.log('🤖 [AI Player] ❌ No affordable transfer targets found');
            }
            console.log('');

            promotions = promotionCandidates.map(c => ({ playerId: c.playerId }));
            playersToSell = saleCandidates.map(c => ({ playerId: c.playerId }));
            playersToBuy = transferTargets.map(c => ({ playerId: c.playerId }));

            // Build reasoning
            const parts: string[] = [];

            if (lineupChanges.length > 0) {
                parts.push(`Making ${lineupChanges.length} lineup change(s) for the next match`);
            }

            if (promotions.length > 0) {
                parts.push(`promoting ${promotions.length} youth player(s)`);
            }

            if (playersToSell.length > 0) {
                parts.push(`selling ${playersToSell.length} player(s) to free up space/funds`);
            }

            if (playersToBuy.length > 0) {
                parts.push(`targeting ${playersToBuy.length} transfer(s) to strengthen the squad`);
            }

            if (parts.length === 0) {
                reasoning = 'Squad looks solid. No major changes needed this week.';
            } else {
                reasoning = 'Strategic review: ' + parts.join(', ') + '.';
            }
            console.log('🤖 [AI Player] Strategic review complete:', reasoning);
        } else {
            // Regular week: Only lineup changes
            if (lineupChanges.length > 0) {
                reasoning = `Pre-match check: Making ${lineupChanges.length} tactical change(s) to field our strongest XI.`;
            } else {
                reasoning = 'Pre-match check: Team selection looks good. No changes needed.';
            }
            console.log('🤖 [AI Player] Pre-match check complete:', reasoning);
        }

        const result = {
            reasoning,
            lineupChanges,
            promotions: isManagementWeek ? promotions : [],
            playersToSell: isManagementWeek ? playersToSell : [],
            playersToBuy: isManagementWeek ? playersToBuy : [],
            playerConversations
        };

        console.log('🤖 [AI Player] === DECISION SUMMARY ===');
        console.log('🤖 [AI Player] Lineup changes:', result.lineupChanges.length);
        console.log('🤖 [AI Player] Player conversations:', result.playerConversations.length);
        console.log('🤖 [AI Player] Promotions:', result.promotions.length);
        console.log('🤖 [AI Player] Sales:', result.playersToSell.length);
        console.log('🤖 [AI Player] Purchases:', result.playersToBuy.length);
        console.log('🤖 [AI Player] Decision-making complete!');

        return result;
    }
};
